//

import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { orderBy } from '@progress/kendo-data-query';
import * as moment from 'moment';
import { lastValueFrom, map, Observable, tap } from 'rxjs';
import { AuthService } from 'src/app/@auth/auth.service';
import { v4 as uuidv4 } from 'uuid';
import { CluContextItem } from '../@models/clucontext.model';
import { CLU_STORAGE_MODE, CLU_TICKET_STATUS, CluTickItem } from '../@models/clutick.model';
import { CluArtStockItem, CluTickContextItem } from '../@models/clutickcontext.model';
import { RxPDeviceDocumentType } from '../@schemas/p_device.schema';
import { RxCbSysLibreDocument, RxCluTickEnteteDocument, RxCluZTickTraceDocument, RxFArticleDocument } from '../@services/m-cashdesk-core-rxdb';
import { CashDeskCoreRxDbService } from '../@services/m-cashdesk-core-rxdb.service';
import { CluArtGammesPickedResult, CluNomenclatureItem, CluSearchResult } from './cashdesk.models';
import { RxCluTickDuplicataDocumentType } from '../@schemas/clu_tickduplicata.schema';
import { CluDoMoreParams, CluDoMoreParams_DossierItem, CluDoMoreParams_TiersItem } from '../@models/clumoreparams.model';
import { RxFCompteADocumentType } from '../@schemas/f_comptea.schema';
import { CustomNumberPipe } from 'src/app/pipe/customNumber.pipe';
import { ZTicketVaultDbDocumentDaoService, ZTicketVaultDocumentsPerimetre } from '../@zticketvault/zticketvault-db-document-dao.service';
import { AppService } from 'src/app/services/app.service';
import { HttpClient } from '@angular/common/http';
import { TauriService } from 'src/app/services/tauri/tauri.service';
import { TauriCashdeskDatasService } from 'src/app/services/tauri/cashdesk-datas-tauri.service';

@Injectable()
export class ConnectorCashDeskDaoService {

    mdItem : { MD_SITEGID : string, MD_SITE : string, MD_CAISSE : string, MD_CAISSE_LIB : string, MD_CAISSE_NO : string, CO_NO : number, CO_LIB : string, MD_CAISSIERGID : string, MD_CAISSIER_LIB : string, MD_CAISSIER : string, MD_CAISSE_SESSION : string, MD_LOCALGID : string } = { MD_SITEGID : '', MD_SITE : '', MD_CAISSE : '', MD_CAISSE_LIB : '', MD_CAISSE_NO : '', CO_NO : 0, CO_LIB : '', MD_CAISSIERGID : '', MD_CAISSIER_LIB : '', MD_CAISSIER : '', MD_CAISSE_SESSION : '', MD_LOCALGID : '' };

    constructor(
        private _cashDeskRxDbService : CashDeskCoreRxDbService,
        private _authService: AuthService,
        private _snackbar : MatSnackBar,
        private _zTicketVaultDaoService : ZTicketVaultDbDocumentDaoService,
        private _appService: AppService,
        private _tauriService: TauriService,
        private _tauriCashdeskService: TauriCashdeskDatasService
    ) { }

    // #region Platform Utils
    runQuery_doGetClientItem(CT_NUM : string) {
      const authSessionDto: any = this._authService.authenticatedSession$.value!;
      const sageClientsCollaborateurOnly = authSessionDto.FO_PARAMS?.M_CAISSE_PERM?.CASHIER_ITEM?.SAGE_CLIENTS_COLLABORATEUR_ONLY == true;

      if (this._tauriService.isTauri) {
        let queryParams: any = !sageClientsCollaborateurOnly ? { CT_NUM: CT_NUM } : { CT_NUM: CT_NUM, CO_NO: authSessionDto.FO_PARAMS?.M_CAISSE_PERM?.CASHIER_ITEM?.SAGE_CONO, COLLABORATEUR_ONLY: true };
        queryParams = Object.assign({ PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }, queryParams);
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFClientsDatasAction(queryParams).pipe(map(items => items.length > 0 ? items[0] : null)));
      } else {
        const query = !sageClientsCollaborateurOnly ?
                      this._cashDeskRxDbService.db.fclient.findOne({ selector: { "CT_NUM": { "$eq": CT_NUM } }, sort: [] }) :
                      this._cashDeskRxDbService.db.fclient.findOne({ selector: { "CT_NUM": { "$eq": CT_NUM }, "$or" : [{ "CO_NO" : { "$lte" : 0 } }, { "CO_NO" : { "$eq" : authSessionDto.FO_PARAMS?.M_CAISSE_PERM?.CASHIER_ITEM?.SAGE_CONO } }] }, sort: [] });
        return query.exec();
      }
    }

    runQuery_doGetDepotItem(DE_NO : number) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFDepotsDatasAction({ DE_NO: DE_NO, PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }).pipe(map(items => items.length > 0 ? items[0] : null)));
      } else {
        const query = this._cashDeskRxDbService.db.fdepot.findOne({ selector: { "DE_NO": { "$eq": DE_NO } }, sort: [] });
        return query.exec();
      }
    }

    runQuery_doGetSoucheItem(DO_SOUCHE : number) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskPSouchesVenteDatasAction({ DO_SOUCHE: DO_SOUCHE, PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }).pipe(map(items => items.length > 0 ? items[0] : null)));
      } else {
        const query = this._cashDeskRxDbService.db.psouchevente.findOne({ selector: { "DO_SOUCHE": { "$eq": DO_SOUCHE } }, sort: [] });
        return query.exec();
      }
    }

    private runQuery_doGetAffaireItem(CA_NUM : string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFCompteADatasAction({ CA_NUM: CA_NUM, IS_AFFAIRE: 1, PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }).pipe(map(items => items.length > 0 ? items[0] : null)))
      } else {
        const query = this._cashDeskRxDbService.db.fcomptea.findOne({ selector: { "CA_NUM": { "$eq": CA_NUM }, "IS_AFFAIRE" : { "$eq" : 1 } }, sort: [] });
        return query.exec();
      }
    }

    private runQuery_doGetPParamsItem() {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskPParamsDatasAction({ CBMARQ: 1 }));
      } else {
        return this._cashDeskRxDbService.db.pparams.findOne({
            selector: { "CBMARQ": { "$eq": "1" } },
            sort: []
        }).exec();
      }
    }

    private runQuery_doGetFnParamsItem() {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFnParamsDatasAction({ CBMARQ: 1 }));
      } else {
        return this._cashDeskRxDbService.db.fnparams.findOne({
            selector: { "CBMARQ": { "$eq": "1" } },
            sort: []
        }).exec();
      }
    }

    private runQuery_doGetVxParamsItem() {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskVxParamsDatasAction({ CBMARQ: 1 }));
      } else {
        return this._cashDeskRxDbService.db.vxparams.findOne({
            selector: { "CBMARQ": { "$eq": "1" } },
            sort: []
        }).exec();
      }
    }

    private runQuery_doGetDeviseItems() {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskPDevisesDatasAction({ D_MAIN: 1, PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }).pipe(map(items => items.length > 0 ? items[0] : null)));
      } else {
        return this._cashDeskRxDbService.db.pdevise.findOne({
          selector: { "D_MAIN": { "$eq": 1 } },
          sort: []
        }).exec();
      }
    }

    private runQuery_doGetDeviceItem(CAISSE_GID: string) {
      if (this._tauriService.isTauri) {
        return this._tauriService.doGetPDeviceItemByCaissegidAction(CAISSE_GID);
      } else {
        return this._cashDeskRxDbService.db.pdevice.findOne({
            selector: { "CAISSEGID": { "$eq": CAISSE_GID } },
            sort: []
        }).exec();
      }
    }

    private runQuery_doGetDepotPrincipalItem() {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFDepotsDatasAction({ DE_PRINCIPAL_F: true, PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }).pipe(map(items => items.length > 0 ? items[0] : null)));
      } else {
        return this._cashDeskRxDbService.db.fdepot.findOne({
            selector: { "DE_PRINCIPAL_F": { "$eq": true } }, sort: []
        }).exec();
      }
    }

    private runQuery_doGetSoucheVentePrincipaleItem() {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskPSouchesVenteDatasAction({ SV_PRINCIPAL_F: true, PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }).pipe(map(items => items.length > 0 ? items[0] : null)));
      } else {
        return this._cashDeskRxDbService.db.psouchevente.findOne({
            selector: { "SV_PRINCIPAL_F": { "$eq": true } }, sort: []
        }).exec();
      }
    }

    private runQuery_doGetTarifItems() {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFTarifsDatasAction());
      } else {
        return this._cashDeskRxDbService.db.ftarif.find().exec();
      }
    }

    private runQuery_doGetTarifselectItems() {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFTarifsSelectDatasAction());
      } else {
        return this._cashDeskRxDbService.db.ftarifselect.find().exec();
      }
    }

    private runQuery_doGetTicketCurrentPieceItem(MD_SITEGID: string, MD_CAISSE: string) {
      if (this._tauriService.isTauri) {
        return this._tauriService.doFindOneTickCurrentPieceAction({
          MD_SITEGID: MD_SITEGID,
          MD_CAISSE: MD_CAISSE
        });
      } else {
        return this._cashDeskRxDbService.db.clutickcurrentpiece.findOne({
            selector: {
                "MD_SITEGID": { "$eq": MD_SITEGID },
                "MD_CAISSE": { "$eq": MD_CAISSE }
            },
            sort: []
        }).exec();
      }
    }

    private runQuery_doGetCollaborateurItem(coNo: number) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFCollaborateursDatasAction({ CO_NO : coNo }).pipe(map(items => items.length > 0 ? items[0] : null)));
      } else {
        return this._cashDeskRxDbService.db.fcollaborateur.findOne({ selector : { CO_NO : { $eq : coNo } } }).exec();
      }
    }

    private async runQuery_doSearchArticleItems(_search: string) {
      let ARTICLES_STARTSWITHMATCHES: any[] = [];
      let ARTICLES_PARTIALMATCHES: any[] = [];
      if (this._tauriService.isTauri) {
        ARTICLES_STARTSWITHMATCHES = await lastValueFrom(this._tauriCashdeskService.doGetCashdeskFArticlesDatasAction({ SEARCH: `%${_search}%` }));
        ARTICLES_PARTIALMATCHES = await lastValueFrom(this._tauriCashdeskService.doGetCashdeskFArticlesDatasAction({ SEARCH: _search, PAGENUMBER: 1, PAGESIZE: 150, PAGINATED: true }));
      } else {
        ARTICLES_STARTSWITHMATCHES = await this._cashDeskRxDbService.db.farticle.find({
            selector: {
                $or: [
                    { AR_REF : { $regex: `^${_search}`, $options: 'i' } },
                    { AR_DESIGN : { $regex: `^${_search}`, $options: 'i' } },
                    { AR_CODEBARRE : { $regex: `^${_search}`, $options: 'i' } }
                ]
            }
        }).exec();
        ARTICLES_PARTIALMATCHES = await this._cashDeskRxDbService.db.farticle.find({
            selector: {
                $or: [
                    { AR_REF : { $regex: _search, $options: 'i' } },
                    { AR_DESIGN : { $regex: _search, $options: 'i' } },
                    { AR_CODEBARRE : { $regex: _search, $options: 'i' } }
                ]
            },
            sort: [],
            limit : 150
        }).exec();
      }

      return [...ARTICLES_STARTSWITHMATCHES, ...ARTICLES_PARTIALMATCHES.filter(item => !ARTICLES_STARTSWITHMATCHES.some(m=> m.AR_REF == item.AR_REF) )];
    }

    private runQuery_doSearchClientItems(_search: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFClientsDatasAction({ SEARCH: _search, PAGENUMBER: 1, PAGESIZE: 150, PAGINATED: true }));
      } else {
        return this._cashDeskRxDbService.db.fclient.find({
            selector: {
                $or: [
                    { CT_NUM : { $regex: _search, $options: 'i' } },
                    { CT_INTITULE : { $regex: _search, $options: 'i' } },
                    { CT_TELEPHONE: { $regex: this._buildPhoneRegex(_search), $options: 'i' } }
                ]
            },
            sort: [],
            limit : 150
        }).exec();
      }
    }

    private runQuery_doGetArtEnumRefItemByAECodeBarre(_codebarre: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFArtEnumRefsDatasAction({ AE_CODEBARRE: _codebarre }));
      } else {
        return this._cashDeskRxDbService.db.fartenumref.find({
            selector: {
                $or: [
                    { AE_CODEBARRE : { $regex: _codebarre, $options: 'i' } }
                ]
            },
            sort: [],
            limit : 150
        }).exec();
      }
    }

    private runQuery_doGetConditionItemByARCodeBarre(_codebarre: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFConditionsDatasAction({ CO_CODEBARRE: _codebarre }));
      } else {
        return this._cashDeskRxDbService.db.fcondition.find({
            selector: {
                $or: [
                    { CO_CODEBARRE : { $regex: _codebarre, $options: 'i' } }
                ]
            },
            sort: [],
            limit : 150
        }).exec();
      }
    }

    private TrunQuery_doGetNomenclatureItems(_arRef: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFNomenclaturesDatasAction({ AR_REF: _arRef }));
      } else {
        return this._cashDeskRxDbService.db.fnomenclature.find({
            selector: { AR_REF : { $eq: _arRef } },
            sort: []
        }).exec();
      }
    }

    private runQuery_doGetArticleItemByArRef(_arRef: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFArticlesDatasAction({ AR_REF: _arRef, PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }).pipe(map(items => items.length > 0 ? items[0] : null)));
      } else {
        return this._cashDeskRxDbService.db.farticle.findOne({
            selector: { AR_REF : { $eq: _arRef } },
            sort: []
        }).exec();
      }
    }

    private runQuery_doGetClientItemByCtNum(_ctNum: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFClientsDatasAction({ CT_NUM: _ctNum }).pipe(map(items => items.length > 0 ? items[0] : null)));
      } else {
        return this._cashDeskRxDbService.db.fclient.findOne({ selector: { "CT_NUM": { "$eq": _ctNum } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetArtClientItems(_arref: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFArtClientDatasAction({ AR_REF: _arref }));
      } else {
        return this._cashDeskRxDbService.db.fartclient.find({ selector: { "AR_REF": { "$eq": _arref } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetArtComptaItems(_arref: string) {
      if (this._tauriService.isTauri) {
         return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFArtComptaDatasAction({ AR_REF: _arref }));
      } else {
        return this._cashDeskRxDbService.db.fartcompta.find({ selector: { "AR_REF": { "$eq": _arref } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetTarifgamItems(_arref: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFTarifgamDatasAction({ AR_REF: _arref }));
      } else {
        return this._cashDeskRxDbService.db.ftarifgam.find({ selector: { "AR_REF": { "$eq": _arref } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetTarifqteItems(_arref: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFTarifqteDatasAction({ AR_REF: _arref }));
      } else {
        return this._cashDeskRxDbService.db.ftarifqte.find({ selector: { "AR_REF": { "$eq": _arref } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetConditionItems(_arref: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFConditionsDatasAction({ AR_REF: _arref }));
      } else {
        return this._cashDeskRxDbService.db.fcondition.find({ selector: { "AR_REF": { "$eq": _arref } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetTarifcondItems(_arref: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFTarifcondDatasAction({ AR_REF: _arref }));
      } else {
        return this._cashDeskRxDbService.db.ftarifcond.find({ selector: { "AR_REF": { "$eq": _arref } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetFamcomptaItems(_codefamille?: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFFamcomptaDatasAction({ FA_CODEFAMILLE: _codefamille }));
      } else {
        return this._cashDeskRxDbService.db.ffamcompta.find({ selector: { "FA_CODEFAMILLE": { "$eq": _codefamille } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetFamclientItems(_ctnum: string, _codefamille?: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFFamclientDatasAction({ CT_NUM: _ctnum, FA_CODEFAMILLE: _codefamille }));
      } else {
        return this._cashDeskRxDbService.db.ffamclient.find({ selector: { "CT_NUM": { "$eq": _ctnum }, "FA_CODEFAMILLE": { "$eq": _codefamille } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetFamtarifItems(_codefamille?: string, _ftcategorie?: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFFamtarifDatasAction({ FA_CODEFAMILLE: _codefamille, FT_CATEGORIE: _ftcategorie }));
      } else {
        return this._cashDeskRxDbService.db.ffamtarif.find({ selector: { "FA_CODEFAMILLE": { "$eq": _codefamille }, "FT_CATEGORIE": { "$eq": _ftcategorie } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetFamtarifqteItems(_fqrefcf: string[], _codefamille?: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFFamtarifqteDatasAction({ FA_CODEFAMILLE: _codefamille, FQ_REFCF: _fqrefcf }));
      } else {
        return this._cashDeskRxDbService.db.ffamtarifqte.find({ selector: { "FA_CODEFAMILLE": { "$eq": _codefamille }, "FQ_REFCF": { "$in": _fqrefcf } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetModeleItems() {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFModeleDatasAction());
      } else {
        return this._cashDeskRxDbService.db.fmodele.find({ selector: {}, sort: [] }).exec();
      }
    }

    private runQuery_doGetArtModeleItems(_arref: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskFArtModeleDatasAction({ AR_REF: _arref }));
      } else {
        return this._cashDeskRxDbService.db.fartmodele.find({ selector: { "AR_REF": { "$eq": _arref } }, sort: [] }).exec();
      }
    }

    private runQuery_doGetCbsyslibreItems(_cbfile?: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskCbsyslibreDatasAction({ CB_FILE: _cbfile }));
      } else {
        return this._cashDeskRxDbService.db.cbsyslibre.find({ selector: { 'CB_FILE' : { "$eq" : _cbfile } }, sort: [] }).exec()
      }
    }

    private runQuery_doGetPDossierItem() {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskPDossierDatasAction());
      } else {
        return this._cashDeskRxDbService.db.pdossier.findOne().exec();
      }
    }

    private runQuery_doGetCluzticktraceItems(_dopieceinternal: string) {
      if (this._tauriService.isTauri) {
        return lastValueFrom(this._tauriCashdeskService.doGetCashdeskCluzticktraceAction({ DO_PIECE_INTERNAL: _dopieceinternal }));
      } else {
        return this._cashDeskRxDbService.db.cluzticktrace.find({
            selector: {
                "DO_PIECE_INTERNAL": { "$eq": _dopieceinternal }
            }
        }).exec();
      }
    }
    // #endregion

    async runQuery_doGetCashDeskItem() : Promise<CluContextItem> {
        //await this.runQuery_doCheckAndRestoreTicketsFromVault();
        const authSessionDto: any = this._authService.authenticatedSession$.value!;
        const DEVICE = await this.runQuery_doGetDeviceItem(localStorage.getItem('LS_FE_AUTH_CASHDESK_GID')!);
        let PARAMS = await this.runQuery_doGetPParamsItem();
        let FN_PARAMS = await this.runQuery_doGetFnParamsItem();
        let VX_PARAMS = await this.runQuery_doGetVxParamsItem();
        let DEVISE = await this.runQuery_doGetDeviseItems();
        let DEPOT_PRINCIPAL = await this.runQuery_doGetDepotPrincipalItem();
        // DEPOT PRINCIPAL : USER > SITE
        if(authSessionDto && authSessionDto.FO_PARAMS && authSessionDto.FO_PARAMS.M_CAISSE_PERM && authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM && authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM.SAGE_DE_NO_DEFAULT && authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM.SAGE_DE_NO_DEFAULT > 0) {
            let DEPOT_CASHIER = await this.runQuery_doGetDepotItem(authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM.SAGE_DE_NO_DEFAULT);
            if(DEPOT_CASHIER) { DEPOT_PRINCIPAL = DEPOT_CASHIER; }
        }
        let SOUCHEVENTE_PRINCIPALE = await this.runQuery_doGetSoucheVentePrincipaleItem();
        // SOUCHE PRINCIPALE : USER > DEVICE > SITE
        if(DEVICE && DEVICE.C_DO_SOUCHE && DEVICE.C_DO_SOUCHE > 0) {
            let SOUCHE_DEVICE = await this.runQuery_doGetSoucheItem(DEVICE.C_DO_SOUCHE);
            if(SOUCHE_DEVICE) { SOUCHEVENTE_PRINCIPALE = SOUCHE_DEVICE; }
        }
        let AFFAIRE_PRINCIPALE = null;
        if(DEVICE && DEVICE.C_N_ANALYTIQUE && DEVICE.C_N_ANALYTIQUE > 0 && DEVICE.C_CA_NUM) {
            let AFFAIRE_DEVICE = await this.runQuery_doGetAffaireItem(DEVICE.C_CA_NUM);
            if(AFFAIRE_DEVICE) { AFFAIRE_PRINCIPALE = AFFAIRE_DEVICE; }
        }
        if(authSessionDto && authSessionDto.FO_PARAMS && authSessionDto.FO_PARAMS.M_CAISSE_PERM && authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM && authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM.SAGE_DO_SOUCHE_DEFAULT && authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM.SAGE_DO_SOUCHE_DEFAULT > 0) {
            let SOUCHE_CASHIER = await this.runQuery_doGetSoucheItem(authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM.SAGE_DO_SOUCHE_DEFAULT);
            if(SOUCHE_CASHIER) { SOUCHEVENTE_PRINCIPALE = SOUCHE_CASHIER; }
        }
        let TARIF_ITEMS = await this.runQuery_doGetTarifItems();
        let TARIFSELECT_ITEMS = await this.runQuery_doGetTarifselectItems();

        let cluContextItem = new CluContextItem();
        cluContextItem.PARAMS = PARAMS;
        cluContextItem.FN_PARAMS = FN_PARAMS;
        cluContextItem.VX_PARAMS = VX_PARAMS;
        cluContextItem.DEVISE = DEVISE;
        cluContextItem.DEVICE = DEVICE;
        cluContextItem.DEPOT_PRINCIPAL = DEPOT_PRINCIPAL;
        cluContextItem.SOUCHEVENTE_PRINCIPALE = SOUCHEVENTE_PRINCIPALE;
        cluContextItem.AFFAIRE_PRINCIPALE = AFFAIRE_PRINCIPALE;
        cluContextItem.TARIF_ITEMS = TARIF_ITEMS;
        cluContextItem.TARIFSELECT_ITEMS = TARIFSELECT_ITEMS;

        // #region META DATA
        if(cluContextItem.DEVICE && cluContextItem.DEVICE.CAISSEGID){
            let SS_LINKUP_WORKSPACE_AUTH_SESSION_KEY = localStorage.getItem('SS_LINKUP_WORKSPACE_AUTH_SESSION_KEY') || '';
            this.mdItem.MD_SITEGID = authSessionDto.SITE_ITEM.SITEGID;
            this.mdItem.MD_SITE = authSessionDto.SITE_ITEM.SITE;
            this.mdItem.MD_CAISSE = localStorage.getItem('LS_FE_AUTH_CASHDESK_GID') || '';
            this.mdItem.MD_CAISSE_NO = cluContextItem.DEVICE.C_NO;
            this.mdItem.MD_CAISSE_LIB = cluContextItem.DEVICE.C_NO + ' ' + cluContextItem.DEVICE.C_DENOMINATION;
            this.mdItem.CO_NO = authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM.SAGE_CONO;
            this.mdItem.CO_LIB = authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM.SAGE_COLIB;
            this.mdItem.MD_CAISSIERGID = authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM.CAISSIERGID;
            this.mdItem.MD_CAISSIER_LIB = authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM.CLU_USERLIB;
            this.mdItem.MD_CAISSIER = authSessionDto.FO_PARAMS.M_CAISSE_PERM.CASHIER_ITEM.CLU_USERGID;
            this.mdItem.MD_CAISSE_SESSION = cluContextItem.DEVICE.CAISSE_SESSION || '';
            this.mdItem.MD_LOCALGID = cluContextItem.DEVICE.CAISSE_SESSION_OUVERTURE_PAR_LOCALGID ?? '';

            let DEVISE_FORMAT = cluContextItem.DEVISE && cluContextItem.DEVISE.D_FORMAT ? cluContextItem.DEVISE.D_FORMAT : '# ##0,00';
            sessionStorage.setItem('CAURIS_LINKUP_FORMAT', DEVISE_FORMAT);

            localStorage.setItem('LS_FE_AUTH_CASHDESK_SESSION_LOCALGID', cluContextItem.DEVICE.CAISSE_SESSION_OUVERTURE_PAR_LOCALGID ?? '');
            localStorage.setItem('LS_FE_AUTH_CASHDESK_SESSION', cluContextItem.DEVICE.CAISSE_SESSION ?? '');
        }
        // #endregion
        return cluContextItem;
    }

    async runQuery_doSaveRxDeviceItem(_deviceItem : RxPDeviceDocumentType) : Promise<any> {
      if (this._tauriService.isTauri) {
        let DEVICE: any = null;
        try { DEVICE = await this._tauriService.doGetPDeviceItemByCaissegidAction(_deviceItem.CAISSEGID); } catch {}
        if(!!DEVICE) {
          DEVICE.SITE_SESSION = _deviceItem.SITE_SESSION;
          DEVICE.SITE_SESSION_ETAT = _deviceItem.SITE_SESSION_ETAT;
          DEVICE.SITE_SESSION_OUVERTURE = _deviceItem.SITE_SESSION_OUVERTURE;
          DEVICE.CAISSE_SESSION = _deviceItem.CAISSE_SESSION;
          DEVICE.CAISSE_SESSION_ETAT = _deviceItem.CAISSE_SESSION_ETAT;
          DEVICE.CAISSE_SESSION_OUVERTURE = _deviceItem.CAISSE_SESSION_OUVERTURE;
          DEVICE.CAISSE_SESSION_OUVERTURE_PAR_LOCALGID = _deviceItem.CAISSE_SESSION_OUVERTURE_PAR_LOCALGID;
          return await this._tauriService.doUpdatePDeviceItemAction(DEVICE.ID, DEVICE);
        }
      } else {
        const query_DEVICE = this._cashDeskRxDbService.db.pdevice.findOne({ selector: { "CAISSEGID": { "$eq": _deviceItem.CAISSEGID } }, sort: [] });
        let DEVICE = await query_DEVICE.exec();
        return await DEVICE?.update({ $set : {
            'SITE_SESSION' : _deviceItem.SITE_SESSION,
            'SITE_SESSION_ETAT' : _deviceItem.SITE_SESSION_ETAT,
            'SITE_SESSION_OUVERTURE' : _deviceItem.SITE_SESSION_OUVERTURE,
            'CAISSE_SESSION' : _deviceItem.CAISSE_SESSION,
            'CAISSE_SESSION_ETAT' : _deviceItem.CAISSE_SESSION_ETAT,
            'CAISSE_SESSION_OUVERTURE' : _deviceItem.CAISSE_SESSION_OUVERTURE,
            'CAISSE_SESSION_OUVERTURE_PAR_LOCALGID' : _deviceItem.CAISSE_SESSION_OUVERTURE_PAR_LOCALGID
        }});
      }
    }

    async runQuery_doGetCluTickItem(DO_PIECE_INTERNAL : string) : Promise<CluTickItem> {
        if (this._tauriService.isTauri) {
            const cluTickItem = await this._tauriService.doGetCluTickItemAction(DO_PIECE_INTERNAL);
            const parsedCluTickItem = Object.assign(new CluTickItem(), cluTickItem);
            return parsedCluTickItem;
        } else {
            const query_ENTETE = this._cashDeskRxDbService.db.clutickentete.findOne({
                selector: {
                    "DO_PIECE_INTERNAL": { "$eq": DO_PIECE_INTERNAL }
                },
                sort: []
            });
            const query_LIGNES = this._cashDeskRxDbService.db.clutickligne.find({
                selector: {
                    "DO_PIECE_INTERNAL": { "$eq": DO_PIECE_INTERNAL }
                },
                sort: [{ DL_LIGNE: 'asc' }]
            });
            const query_REGLEMENTS = this._cashDeskRxDbService.db.clutickreglement.find({
                selector: {
                    "DO_PIECE_INTERNAL": { "$eq": DO_PIECE_INTERNAL }
                },
                sort: []
            });
            let ENTETE = await query_ENTETE.exec();
            if (ENTETE == undefined) { let _error = 'Le document ' + DO_PIECE_INTERNAL +  ' ne semble pas exister.'; this.displaySnackbarErrorMessage("ERROR", _error); throw Error(_error); }
            let LIGNES = await query_LIGNES.exec();
            let REGLEMENTS = await query_REGLEMENTS.exec();
            let cluTickItem = new CluTickItem();
            cluTickItem.ENTETE = JSON.parse(JSON.stringify(ENTETE));
            cluTickItem.LIGNES = JSON.parse(JSON.stringify(LIGNES));
            cluTickItem.REGLEMENTS = JSON.parse(JSON.stringify(REGLEMENTS));
            return cluTickItem;
        }
    }

    runQuery_doGetCluTickEnteteObservableItem(DO_PIECE_INTERNAL : string) : Observable<RxCluTickEnteteDocument | null> {
      if (this._tauriService.isTauri) {
        return this._tauriService.cluTicketEnteteObservable$.asObservable();
      } else {
        const query_ENTETE = this._cashDeskRxDbService.db.clutickentete.findOne({
            selector: { "DO_PIECE_INTERNAL": { "$eq": DO_PIECE_INTERNAL } },
            sort: []
        });
        return query_ENTETE.$.pipe( tap(() => { }) );
      }
    }

    async runQuery_doCreateCluTicketItem(CT_NUM : string, DE_NO : number, DO_SOUCHE : number, CA_NUM : string | undefined) : Promise<CluTickItem> {
        const authSessionDto: any = this._authService.authenticatedSession$.value!;
        let res_EMPTY_OR_ACTIVE_PIECE: any = null;
        let activeTicketItem: any = null;
        if (this._tauriService.isTauri) {
            let activeTickets = await this._tauriService.doGetCluTickEntetesAction({
                MD_SITEGID: this.mdItem.MD_SITEGID,
                MD_CAISSE: this.mdItem.MD_CAISSE,
                MD_CAISSE_SESSION: this.mdItem.MD_CAISSE_SESSION,
                MD_CAISSIERGID: this.mdItem.MD_CAISSIERGID,
                ZTICKET_STATUS: [CLU_TICKET_STATUS.TICKET_ACTIVE],
            });
            res_EMPTY_OR_ACTIVE_PIECE = activeTickets.length > 0 ? activeTickets[0] : null;
            if (!res_EMPTY_OR_ACTIVE_PIECE) {
                activeTickets = await this._tauriService.doGetCluTickEntetesAction({
                    MD_SITEGID: this.mdItem.MD_SITEGID,
                    MD_CAISSE: this.mdItem.MD_CAISSE,
                    MD_CAISSE_SESSION: this.mdItem.MD_CAISSE_SESSION,
                    MD_CAISSIERGID: this.mdItem.MD_CAISSIERGID,
                    DO_SOLDETTC: 0,
                    ZTICKET_STATUS: [CLU_TICKET_STATUS.TICKET_PAUSED]
                });
                res_EMPTY_OR_ACTIVE_PIECE = activeTickets.length > 0 ? activeTickets[0] : null;
            }
            if (res_EMPTY_OR_ACTIVE_PIECE) {
                const ticketItem = await this._tauriService.doGetCluTickItemAction(res_EMPTY_OR_ACTIVE_PIECE.DO_PIECE_INTERNAL)
                activeTicketItem = Object.assign(new CluTickItem(), ticketItem);
            }
        } else {
            const query_EMPTY_OR_ACTIVE_PIECE = this._cashDeskRxDbService.db.clutickentete.findOne({
                selector: {
                    $or: [
                        {
                            "MD_SITEGID": { "$eq": this.mdItem.MD_SITEGID },
                            "MD_CAISSE": { "$eq": this.mdItem.MD_CAISSE },
                            "MD_CAISSE_SESSION": { "$eq": this.mdItem.MD_CAISSE_SESSION },
                            "MD_CAISSIERGID": { "$eq": this.mdItem.MD_CAISSIERGID },
                            "DO_SOLDETTC": { "$eq": 0 },
                            "ZTICKET_STATUS": { "$eq": CLU_TICKET_STATUS.TICKET_PAUSED }
                        },
                        {
                            "MD_SITEGID": { "$eq": this.mdItem.MD_SITEGID },
                            "MD_CAISSE": { "$eq": this.mdItem.MD_CAISSE },
                            "MD_CAISSE_SESSION": { "$eq": this.mdItem.MD_CAISSE_SESSION },
                            "MD_CAISSIERGID": { "$eq": this.mdItem.MD_CAISSIERGID },
                            "ZTICKET_STATUS": { "$eq": CLU_TICKET_STATUS.TICKET_ACTIVE }
                        }
                    ]
                },
                sort: []
            });
            res_EMPTY_OR_ACTIVE_PIECE = await query_EMPTY_OR_ACTIVE_PIECE.exec();
            if(res_EMPTY_OR_ACTIVE_PIECE){ activeTicketItem = await this.runQuery_doGetCluTickItem(res_EMPTY_OR_ACTIVE_PIECE.DO_PIECE_INTERNAL); }
        }
        if (activeTicketItem) {
            return activeTicketItem;
        } else {
            let CLIENT = await this.runQuery_doGetClientItem(CT_NUM);
            if(!CLIENT) { let _error = 'Client divers inexistant. Vérifiez votre paramètrage et pensez à effectuer une synchro.'; this.displaySnackbarErrorMessage("ERROR", _error); throw Error(_error); }

            let DEPOT = await this.runQuery_doGetDepotItem(DE_NO);
            if(!DEPOT) { let _error = 'Dépôt inexistant. Vérifiez votre paramètrage et pensez à effectuer une synchro.'; this.displaySnackbarErrorMessage("ERROR", _error); throw Error(_error); }

            let SOUCHE = await this.runQuery_doGetSoucheItem(DO_SOUCHE);
            if(!SOUCHE) { let _error = 'Souche inexistante. Vérifiez votre paramètrage et pensez à effectuer une synchro.'; this.displaySnackbarErrorMessage("ERROR", _error); throw Error(_error); }

            let AFFAIRE : RxFCompteADocumentType | null = null;
            if(CA_NUM) {
                AFFAIRE = await this.runQuery_doGetAffaireItem(CA_NUM);
                if(!AFFAIRE) { let _error = 'Affaire inexistante. Vérifiez votre paramètrage et pensez à effectuer une synchro.'; this.displaySnackbarErrorMessage("ERROR", _error); throw Error(_error); }
            }

            let res_CURRENTPIECE: any = await this.runQuery_doGetTicketCurrentPieceItem(this.mdItem.MD_SITEGID, this.mdItem.MD_CAISSE);
            let PREFIXPIECE = 'T';
            if (this._appService.isFrontofficeTestEnv) {
                PREFIXPIECE = 'X';
            }
            let CAISSE_SESSION_CODE = '';
            for(var c of this.mdItem.MD_CAISSE_SESSION.substring(0,3).toUpperCase()) {
                if(c == '0') { CAISSE_SESSION_CODE += c; }
                else if(/^\d+$/.test(c)) { CAISSE_SESSION_CODE += String.fromCharCode(parseInt(c) + 64); }
                else { CAISSE_SESSION_CODE += c; }
            }
            let CURRENTPIECE_NEXT : string = '';
            if(res_CURRENTPIECE){
                CURRENTPIECE_NEXT = PREFIXPIECE + this.mdItem.MD_CAISSE_NO + CAISSE_SESSION_CODE + moment().format('YYMMDD') + ('000000' + (parseInt(res_CURRENTPIECE.DC_PIECE.slice(-6)) + 1)).slice(-6);
                if (this._tauriService.isTauri) {
                  await this._tauriService.doUpdateCluTickCurrentPieceAction(res_CURRENTPIECE.PK_ID, {
                    ID: res_CURRENTPIECE.ID,
                    PK_ID : res_CURRENTPIECE.PK_ID,
                    MD_SITEGID : this.mdItem.MD_SITEGID,
                    MD_SITE : this.mdItem.MD_SITE,
                    MD_CAISSE : this.mdItem.MD_CAISSE,
                    MD_CAISSE_SESSION : this.mdItem.MD_CAISSE_SESSION,
                    DC_PIECE : CURRENTPIECE_NEXT
                  });
                } else {
                  await res_CURRENTPIECE.patch({ MD_SITEGID : this.mdItem.MD_SITEGID, MD_SITE : this.mdItem.MD_SITE, MD_CAISSE : this.mdItem.MD_CAISSE, MD_CAISSE_SESSION : this.mdItem.MD_CAISSE_SESSION, DC_PIECE : CURRENTPIECE_NEXT });
                }
            }
            else {
              let DEVICE: any = await this.runQuery_doGetDeviceItem(this.mdItem.MD_CAISSE);
              let SEQUENCE = 1; if (DEVICE && DEVICE.LASTSEQ_TICKET && DEVICE.LASTSEQ_TICKET > 0) { SEQUENCE = DEVICE.LASTSEQ_TICKET + 1; }
              let SEQUENCE_STR = SEQUENCE.toString().padStart(6, '0');
              CURRENTPIECE_NEXT = PREFIXPIECE + this.mdItem.MD_CAISSE_NO + CAISSE_SESSION_CODE + moment().format('YYMMDD') + SEQUENCE_STR;
              if (this._tauriService.isTauri) {
                await this._tauriService.doCreateTickCurrentPieceAction({
                  PK_ID : uuidv4(),
                  MD_SITEGID : this.mdItem.MD_SITEGID,
                  MD_SITE : this.mdItem.MD_SITE,
                  MD_CAISSE : this.mdItem.MD_CAISSE,
                  MD_CAISSE_SESSION : this.mdItem.MD_CAISSE_SESSION,
                  DC_PIECE : CURRENTPIECE_NEXT
                });
              } else {
                await this._cashDeskRxDbService.db.clutickcurrentpiece.insert({ PK_ID : uuidv4(), MD_SITEGID : this.mdItem.MD_SITEGID, MD_SITE : this.mdItem.MD_SITE, MD_CAISSE : this.mdItem.MD_CAISSE, MD_CAISSE_SESSION : this.mdItem.MD_CAISSE_SESSION, DC_PIECE : CURRENTPIECE_NEXT });
              }
            }
            // #region COLLABORATEUR
            let CO_NO = this.mdItem.CO_NO;
            let CO_LIB = this.mdItem.CO_LIB;

            let DEVICE: RxPDeviceDocumentType | null = await this.runQuery_doGetDeviceItem(this.mdItem.MD_CAISSE);
            switch(DEVICE?.C_CO_NO_MODE) {
              // COLLABORATEUR PRIORITÉ A LA FICHE CLIENT
              case 1 :
                  let CLIENT = await this.runQuery_doGetClientItem(CT_NUM);
                  if(CLIENT && CLIENT.CO_NO && CLIENT.CO_NO > 0) {
                      let COLLABORATEUR = await this.runQuery_doGetCollaborateurItem(CLIENT.CO_NO);
                      if(COLLABORATEUR) { CO_NO = COLLABORATEUR.CO_NO; CO_LIB = COLLABORATEUR.CO_LIB!; }
                  }
                  break;
              // COLLABORATEUR SPÉCIFIQUE
              case 2 :
                  let COLLABORATEUR = await this.runQuery_doGetCollaborateurItem(DEVICE.C_CO_NO!);
                  if(COLLABORATEUR) { CO_NO = COLLABORATEUR.CO_NO; CO_LIB = COLLABORATEUR.CO_LIB!; }
                  break;
              // COLLABORATEUR RATTACHÉ AU CAISSIER
              case 0 :
              default :
                  break;
            }

            // #endregion
            let typeOperation = this._appService.isFrontofficeTestEnv ? 'VENTE_TEST' : 'VENTE';
            let ENTETE = {
                MD_DOGID : uuidv4(),
                MD_SITEGID : this.mdItem.MD_SITEGID,
                MD_SITE : this.mdItem.MD_SITE,
                MD_CAISSE : this.mdItem.MD_CAISSE,
                MD_CAISSE_LIB : this.mdItem.MD_CAISSE_LIB,
                MD_CAISSIERGID : this.mdItem.MD_CAISSIERGID,
                MD_CAISSIER : this.mdItem.MD_CAISSIER,
                MD_CAISSIER_LIB : this.mdItem.MD_CAISSIER_LIB,
                MD_CAISSE_SESSION : this.mdItem.MD_CAISSE_SESSION,
                MD_LOCALGID : this.mdItem.MD_LOCALGID,
                MD_DATE_CREATION : moment().local().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z',
                MD_CBMARQ : 0,
                DO_PIECE : '',
                DO_PIECE_INTERNAL : CURRENTPIECE_NEXT,
                DO_DATE : moment().local().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z',
                DO_DATE_UTC : moment().utc().format('YYYYMMDDHHmmss'),
                CO_NO : CO_NO,
                CO_LIB : CO_LIB,
                DO_DEVISE : 0,
                DO_DEVISELIB : '',
                DO_DEVISE_FORMAT : '',
                DO_DEVISE_INV : 0,
                DO_COURS : 0,
                DO_REF : '',
                DO_TIERS : CLIENT.CT_NUM,
                CT_INTITULE : CLIENT.CT_INTITULE,
                CT_TAUX01 : CLIENT.CT_TAUX01,
                DO_REM_CONFLICT_MODE : 0,
                DO_TOTALHT : 0,
                DO_TOTALHTNET : 0,
                DO_TOTALTTC : 0,
                DO_TOTALTTC_P : 0,
                DO_SOLDETTC : 0,
                DO_NETAPAYER : 0,
                DO_MONTANTREGLE : 0,
                ZSYNCUP_FLAG : 0,
                ZSYNCUP_STATUS : 'pending',
                ZSYNCUP_ERROR_MESSAGE : '',
                ZSYNCUP_DATETIME : '',
                ZSYNCUP_FEEDID : '',
                ZTICKET_STATUS : CLU_TICKET_STATUS.TICKET_ACTIVE,
                ZSTORAGE_MODE : CLU_STORAGE_MODE.LOCAL,
                ZSYNCAGENTUP_FLAG: 0,
                ZSYNCAGENTUP_STATUS: '',
                ZSYNCAGENTUP_ERROR_MESSAGE: '',
                ZSYNCAGENTUP_DATETIME: '',
                ZSYNCAGENTUP_FEEDID: '',
                LC_CARTE_NUM : '',
                DE_NO : DEPOT.DE_NO,
                DE_INTITULE : DEPOT.DE_INTITULE,
                DO_SOUCHE : SOUCHE.DO_SOUCHE,
                DO_SOUCHELIB : SOUCHE.SV_INTITULE,
                CA_NUM : AFFAIRE ? AFFAIRE.CA_NUM : '',
                CA_INTITULE : AFFAIRE ? AFFAIRE.CA_INTITULE : '',
                DO_PRINT_NB : 0,
                DO_PRINT_AT : '',
                DO_PRINT_LAST : '',
                DO_EMAIL_NB : 0,
                DO_EMAIL_AT : '',
                DO_EMAIL_LAST : '',
                DO_EMAIL_1 : '',
                DO_EMAIL_2 : '',
                DO_EMAIL_3 : '',

                DO_TYPE_OP : typeOperation,
                DO_NB_LIGNES : 0,
                DO_TOT_QTE : 0,
                CLU_APP_VERSION : authSessionDto.APP_VERSION!,
                DO_PIECE_INTERNAL_PREVIOUS : '',
                DO_SIGNATURE_OFFLINE : 0,
                DO_SIGNATURE_REPORT : 0,
                DO_SIGNATURE : '',
                DO_SIGNATURE_PREVIOUS : '',
                DO_TAXE_ITEMS_JSON : '',
                DO_SIGNATURE_DATA : '',

                DO_RG_ESP_RECU_TOT : 0,
                DO_RG_ESP_RENDU_TOT : 0,
                DO_RG_MONTANTECART : 0,
                DO_MONTANTREGLE_F : 0,

                DO_MOREPARAMS_JSON : JSON.stringify(await this.runQuery_doGetCluDoMoreParams(CLIENT.CT_NUM))
            };

            let _tickItem: any = null;
            if (this._tauriService.isTauri) {
                _tickItem = await this._tauriService.doGetCluTickEnteteByDoPieceInternalAction(ENTETE.DO_PIECE_INTERNAL);
                const tickItemSaved = await this._tauriService.doCreateCluTickItemAction(ENTETE);
                _tickItem = Object.assign(new CluTickItem(), tickItemSaved);
            } else {
                await this._cashDeskRxDbService.db.clutickentete.insert(ENTETE);
                _tickItem = await this.runQuery_doGetCluTickItem(CURRENTPIECE_NEXT);
            }

            await this._zTicketVaultDaoService.vaultUpsertTicket(_tickItem, 'COMMITTED_LOCAL');

            return _tickItem;
        }
    }

    async runQuery_doSaveCluTicketItem(_cluTicketItem : CluTickItem){
        if(!(document.title.indexOf('♛') > -1)) { this.displaySnackbarErrorMessage("WARN", "Votre caisse doit être ouverte dans un seul onglet du navigateur pour éditer un ticket."); return; }

        if (this._tauriService.isTauri) {
            _cluTicketItem.ENTETE.DO_NB_LIGNES = _cluTicketItem.LIGNES.length;
            _cluTicketItem.ENTETE.DO_TOT_QTE = _cluTicketItem.LIGNES.reduce((sum, ligne) => sum + ligne.DL_QTE, 0);
            try {
                let DO_TAXE_ITEMS = _cluTicketItem.GET_BILAN_TAXES(await this._cashDeskRxDbService.db.ftaxe.find().exec());
                _cluTicketItem.ENTETE.DO_TAXE_ITEMS_JSON = JSON.stringify(DO_TAXE_ITEMS);
                _cluTicketItem.ENTETE.DO_TOTALTTC_P = _cluTicketItem.ENTETE.DO_TOTALHT + DO_TAXE_ITEMS.reduce((sum, taxe) => sum + this.doRoundToActiveFormat(taxe.T_MONTANT), 0);
            } catch(e) { console.log(e); }
            _cluTicketItem.ENTETE.ZSYNCUP_FLAG = 0;  _cluTicketItem.ENTETE.ZSYNCUP_STATUS = 'pending';

            await this._tauriService.doSaveCluTickItemAction(_cluTicketItem);

        } else {
            const query_ENTETE = this._cashDeskRxDbService.db.clutickentete.findOne({ selector: { "DO_PIECE_INTERNAL": { "$eq": _cluTicketItem.ENTETE.DO_PIECE_INTERNAL } }, sort: [] });
            let ENTETE = await query_ENTETE.exec();
            _cluTicketItem.ENTETE.DO_NB_LIGNES = _cluTicketItem.LIGNES.length;
            _cluTicketItem.ENTETE.DO_TOT_QTE = _cluTicketItem.LIGNES.reduce((sum, ligne) => sum + ligne.DL_QTE, 0);
            //try { _cluTicketItem.ENTETE.DO_TAXE_ITEMS_JSON = JSON.stringify(_cluTicketItem.GET_BILAN_TAXES(await this._cashDeskRxDbService.db.ftaxe.find().exec())); } catch(e) { console.log(e); }
            try {
                let DO_TAXE_ITEMS = _cluTicketItem.GET_BILAN_TAXES(await this._cashDeskRxDbService.db.ftaxe.find().exec());
                _cluTicketItem.ENTETE.DO_TAXE_ITEMS_JSON = JSON.stringify(DO_TAXE_ITEMS);
                _cluTicketItem.ENTETE.DO_TOTALTTC_P = _cluTicketItem.ENTETE.DO_TOTALHT + DO_TAXE_ITEMS.reduce((sum, taxe) => sum + this.doRoundToActiveFormat(taxe.T_MONTANT), 0);
            } catch(e) { console.log(e); }
            _cluTicketItem.ENTETE.ZSYNCUP_FLAG = 0;  _cluTicketItem.ENTETE.ZSYNCUP_STATUS = 'pending';
            await ENTETE?.patch(_cluTicketItem.ENTETE);

            /*
            const query_LIGNES = this._cashDeskRxDbService.db.clutickligne.find({ selector: { "DO_PIECE_INTERNAL": { "$eq": _cluTicketItem.ENTETE.DO_PIECE_INTERNAL } }, sort: [] });
            await query_LIGNES.remove();
            await this._cashDeskRxDbService.db.clutickligne.bulkInsert(_cluTicketItem.LIGNES).then((v) => { if(v.error && v.error.length > 0) { console.log("LIGNES_ERRORS", v.error); } });
            */
            try {
                const query_LIGNES = this._cashDeskRxDbService.db.clutickligne.find({ selector: { "DO_PIECE_INTERNAL": { "$eq": _cluTicketItem.ENTETE.DO_PIECE_INTERNAL } }, sort: [] });
                await query_LIGNES.remove();
                await this._cashDeskRxDbService.db.clutickligne.bulkUpsert(_cluTicketItem.LIGNES);
            } catch (error) { console.log("LIGNES_ERRORS", error); }

            /*
            const query_REGLEMENTS = this._cashDeskRxDbService.db.clutickreglement.find({ selector: { "DO_PIECE_INTERNAL": { "$eq": _cluTicketItem.ENTETE.DO_PIECE_INTERNAL } }, sort: [] });
            await query_REGLEMENTS.remove();
            await this._cashDeskRxDbService.db.clutickreglement.bulkInsert(_cluTicketItem.REGLEMENTS).then((v) => { if(v.error && v.error.length > 0) { console.log("RG_ERRORS", v.error); } });
            */
            try {
                const query_REGLEMENTS = this._cashDeskRxDbService.db.clutickreglement.find({ selector: { "DO_PIECE_INTERNAL": { "$eq": _cluTicketItem.ENTETE.DO_PIECE_INTERNAL } }, sort: [] });
                await query_REGLEMENTS.remove();
                await this._cashDeskRxDbService.db.clutickreglement.bulkUpsert(_cluTicketItem.REGLEMENTS);
            } catch (error) { console.log("RG_ERRORS", error); }

        }

        await this._zTicketVaultDaoService.vaultUpsertTicket(_cluTicketItem, 'COMMITTED_LOCAL');

    }

    async runQuery_doSaveCluTicketDuplicataItem(_cluTicketDuplicataItem : RxCluTickDuplicataDocumentType) {
        if(!(document.title.indexOf('♛') > -1)) { this.displaySnackbarErrorMessage("WARN", "Votre caisse doit être ouverte dans un seul onglet du navigateur pour effectuer cette action."); return; }

        if (this._tauriService.isTauri) {
            await this._tauriService.doCreateDuplicataItemAction(_cluTicketDuplicataItem);
        } else {
            const query_DUPLICATA = this._cashDeskRxDbService.db.clutickduplicata.findOne({ selector: { "DUP_PIECE_INTERNAL": { "$eq": _cluTicketDuplicataItem.DUP_PIECE_INTERNAL } }, sort: [] });
            let DUPLICATA = await query_DUPLICATA.exec();
            if(DUPLICATA != null) { DUPLICATA.remove(); }
            await this._cashDeskRxDbService.db.clutickduplicata.bulkInsert([_cluTicketDuplicataItem]).then((v) => { if(v.error && v.error.length > 0) { console.log("DUPLICATA_ERRORS", v.error); } });
        }
    }

    private _buildPhoneRegex(input: string): string {

        // retire espaces dans la saisie
        const digits = input.replace(/\s+/g, '');

        // ajoute \s* entre chaque caractère
        return digits.split('').join('\\s*');
    }

    async runQuery_doGetSearchResult(_input : string) : Promise<CluSearchResult> {
        if(_input && (_input.indexOf('+') > -1)) { let _escapedInput = _input.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'); _input = _escapedInput; }

        const ARTICLES = await this.runQuery_doSearchArticleItems(_input);
        let CLIENTS = await this.runQuery_doSearchClientItems(_input);
        const authSessionDto: any = this._authService.authenticatedSession$.value!;
        const sageClientsCollaborateurOnly = authSessionDto.FO_PARAMS?.M_CAISSE_PERM?.CASHIER_ITEM?.SAGE_CLIENTS_COLLABORATEUR_ONLY == true;
        if(sageClientsCollaborateurOnly) { CLIENTS = CLIENTS.filter((c) => c.CO_NO && (c.CO_NO <= 0 || c.CO_NO == authSessionDto.FO_PARAMS?.M_CAISSE_PERM?.CASHIER_ITEM?.SAGE_CONO)); }
        let ARTENUMREFS = await this.runQuery_doGetArtEnumRefItemByAECodeBarre(_input);
        let CONDITIONS = await this.runQuery_doGetConditionItemByARCodeBarre(_input);
        let cluSearchResult = new CluSearchResult();
        cluSearchResult.ARTICLES = ARTICLES;
        cluSearchResult.CLIENTS = CLIENTS;
        cluSearchResult.ARTENUMREFS = ARTENUMREFS;
        cluSearchResult.CONDITIONS = CONDITIONS;
        return cluSearchResult;
    }

    async runQuery_doGetNomenclatureItems(_pItem : RxFArticleDocument) : Promise<CluNomenclatureItem[]> {
        let _nItems : CluNomenclatureItem[] = [];
        let NOMENCLAT_ITEMS = await this.TrunQuery_doGetNomenclatureItems(_pItem.AR_REF);
        for (const item of NOMENCLAT_ITEMS) {
            let _nItem = new CluNomenclatureItem();
            _nItem.PARENT_ARTICLE = _pItem;
            _nItem.NOMENCLATURE = item;
            const ARTICLE_ITEM = await this.runQuery_doGetArticleItemByArRef(item.NO_REFDET);
            if (ARTICLE_ITEM == null) continue;
            _nItem.REF_ARTICLE = ARTICLE_ITEM;
            if(item.AG_NO1 && item.AG_NO1 > 0){
                var _agItem = new CluArtGammesPickedResult();
                _agItem.AG_NO1 = item.AG_NO1;
                _agItem.AG_NO1LIB = item.AG_NO1LIB ?? '';
                _agItem.AG_NO2 = item.AG_NO2 && item.AG_NO2 > 0 ? item.AG_NO2 : 0;
                _agItem.AG_NO2LIB = item.AG_NO2LIB ?? '';
                _nItem.REF_ARTICLE_ARTGAMME = _agItem;
            }
            _nItems.push(_nItem);
        }
        return _nItems;
    }

    async runQuery_doGetTickContextItem(_arref : string, _deno : number, _ctnum : string) : Promise<CluTickContextItem> {
        let ARTICLE = await this.runQuery_doGetArticleItemByArRef(_arref);
        let CLIENT = await this.runQuery_doGetClientItemByCtNum(_ctnum);
        let ARTCLIENT_ITEMS = await this.runQuery_doGetArtClientItems(_arref);
        let ARTCOMPTA_ITEMS = await this.runQuery_doGetArtComptaItems(_arref);
        let TARIFGAM_ITEMS = await this.runQuery_doGetTarifgamItems(_arref);
        let TARIFQTE_ITEMS = await this.runQuery_doGetTarifqteItems(_arref);
        let CONDITION_ITEMS = await this.runQuery_doGetConditionItems(_arref);
        let TARIFCOND_ITEMS = await this.runQuery_doGetTarifcondItems(_arref);
        if(TARIFCOND_ITEMS.length > 0) {
            let TARIFCOND_ITEM_CLIENT = TARIFCOND_ITEMS.find(m=> m.TC_REFCF == CLIENT!.CT_NUM!);
            if(TARIFCOND_ITEM_CLIENT) { TARIFCOND_ITEMS = [ TARIFCOND_ITEM_CLIENT ]; }
            else { TARIFCOND_ITEMS = TARIFCOND_ITEMS.filter(m=> m.TC_REFCF.replace('a', '') == CLIENT!.N_CATTARIF?.toString().padStart(2, '0')); }
        }
        let FAMCOMPTA_ITEMS = await this.runQuery_doGetFamcomptaItems(ARTICLE?.FA_FAMNV1);
        let FAMCLIENT_ITEMS = await this.runQuery_doGetFamclientItems(_ctnum, ARTICLE?.FA_FAMNV1);
        let FAMTARIF_ITEMS = await this.runQuery_doGetFamtarifItems(ARTICLE?.FA_FAMNV1, CLIENT?.N_CATTARIF);
        let FAMTARIFQTE_ITEMS = await this.runQuery_doGetFamtarifqteItems([_ctnum, `a${(CLIENT?.N_CATTARIF ?? 0).toString().padStart(2, '0')}`], ARTICLE?.FA_FAMNV1);
        let TARIF_ITEMS = await this.runQuery_doGetTarifItems();
        let TARIFSELECT_ITEMS = await this.runQuery_doGetTarifselectItems();
        let cluTickContextItem = new CluTickContextItem();
        cluTickContextItem.ARTICLE = ARTICLE!;
        cluTickContextItem.CLIENT = CLIENT!;
        cluTickContextItem.ARTCLIENT_ITEMS = ARTCLIENT_ITEMS;
        cluTickContextItem.ARTCOMPTA_ITEMS = ARTCOMPTA_ITEMS;
        cluTickContextItem.FAMCLIENT_ITEMS = FAMCLIENT_ITEMS;
        cluTickContextItem.FAMCOMPTA_ITEMS = FAMCOMPTA_ITEMS;
        cluTickContextItem.TARIFGAM_ITEMS = TARIFGAM_ITEMS;
        cluTickContextItem.TARIFQTE_ITEMS = TARIFQTE_ITEMS;
        cluTickContextItem.CONDITION_ITEMS = CONDITION_ITEMS;
        cluTickContextItem.TARIFCOND_ITEMS = TARIFCOND_ITEMS;
        cluTickContextItem.TARIF_ITEMS = TARIF_ITEMS;
        cluTickContextItem.TARIFSELECT_ITEMS = TARIFSELECT_ITEMS;
        cluTickContextItem.FAMTARIF_ITEMS = FAMTARIF_ITEMS;
        cluTickContextItem.FAMTARIFQTE_ITEMS = FAMTARIFQTE_ITEMS;
        let MODELE_ITEMS = await this.runQuery_doGetModeleItems();
        let ARTMODELE_ITEMS = await this.runQuery_doGetArtModeleItems(_arref);
        MODELE_ITEMS = MODELE_ITEMS.filter(m=> ARTMODELE_ITEMS.map(w=>w.MO_NO).indexOf(m.MO_NO) > -1);
        cluTickContextItem.MODELE_ITEMS = MODELE_ITEMS;

        let ENTETES = [];
        let CLU_ARTSTOCK_ITEM = new CluArtStockItem();

        if (this._tauriService.isTauri) {
            const DLIGNES = await this._tauriService.doGetCluTickLignesAction({ AR_REF : _arref, DE_NO : _deno, MD_CAISSE_SESSION : this.mdItem.MD_CAISSE_SESSION });
            for(const LIGNE of DLIGNES) {
                const ENTETES = await this._tauriService.doGetCluTickEntetesAction({ DO_PIECE_INTERNAL : LIGNE.DO_PIECE_INTERNAL, MD_CAISSE_SESSION : LIGNE.MD_CAISSE_SESSION });
                const ENTETE = ENTETES.length > 0 ? ENTETES[0] : null;
                if(!ENTETE) continue;
                ENTETES.push(ENTETE);
                if(ENTETE.ZTICKET_STATUS != CLU_TICKET_STATUS.TICKET_CANCELLED){
                    CLU_ARTSTOCK_ITEM.CLU_CURRENT_CASHDESK_QTE_VENTE += LIGNE.DL_QTE;
                    if(ENTETE.ZTICKET_STATUS == CLU_TICKET_STATUS.TICKET_ACTIVE) { CLU_ARTSTOCK_ITEM.CLU_CURRENT_CASHDESK_QTE_ACTIF += LIGNE.DL_QTE; }
                    if(ENTETE.ZTICKET_STATUS == CLU_TICKET_STATUS.TICKET_CLOSED) { CLU_ARTSTOCK_ITEM.CLU_CURRENT_CASHDESK_QTE_CLOSED += LIGNE.DL_QTE; }
                    if(ENTETE.ZTICKET_STATUS == CLU_TICKET_STATUS.TICKET_PAUSED) { CLU_ARTSTOCK_ITEM.CLU_CURRENT_CASHDESK_QTE_PAUSED += LIGNE.DL_QTE; }
                }
            }
        } else {
            const query_DLIGNES = this._cashDeskRxDbService.db.clutickligne.find({ selector: { "AR_REF": { "$eq": _arref }, "DE_NO" : { "$eq" : _deno }, "MD_CAISSE_SESSION" : { "$eq" : this.mdItem.MD_CAISSE_SESSION } }, sort: [] });
            let DLIGNES = await query_DLIGNES.exec();
            for(const LIGNE of DLIGNES) {
                const ENTETE = await this._cashDeskRxDbService.db.clutickentete.findOne({ selector: { "DO_PIECE_INTERNAL": { "$eq": LIGNE.DO_PIECE_INTERNAL }, "MD_CAISSE_SESSION": { "$eq": LIGNE.MD_CAISSE_SESSION } }, sort: [] }).exec();
                if(!ENTETE) continue;
                ENTETES.push(ENTETE);
                if(ENTETE.ZTICKET_STATUS != CLU_TICKET_STATUS.TICKET_CANCELLED){
                    CLU_ARTSTOCK_ITEM.CLU_CURRENT_CASHDESK_QTE_VENTE += LIGNE.DL_QTE;
                    if(ENTETE.ZTICKET_STATUS == CLU_TICKET_STATUS.TICKET_ACTIVE) { CLU_ARTSTOCK_ITEM.CLU_CURRENT_CASHDESK_QTE_ACTIF += LIGNE.DL_QTE; }
                    if(ENTETE.ZTICKET_STATUS == CLU_TICKET_STATUS.TICKET_CLOSED) { CLU_ARTSTOCK_ITEM.CLU_CURRENT_CASHDESK_QTE_CLOSED += LIGNE.DL_QTE; }
                    if(ENTETE.ZTICKET_STATUS == CLU_TICKET_STATUS.TICKET_PAUSED) { CLU_ARTSTOCK_ITEM.CLU_CURRENT_CASHDESK_QTE_PAUSED += LIGNE.DL_QTE; }
                }
            }
        }
        cluTickContextItem.CLU_ARTSTOCK_ITEM = CLU_ARTSTOCK_ITEM;

        return cluTickContextItem;
    }

    async runQuery_doGetChampLibreItems() : Promise<RxCbSysLibreDocument[]> {
        let CHAMP_ITEMS = await this.runQuery_doGetCbsyslibreItems();
        return orderBy(CHAMP_ITEMS, [{ field : 'CB_FILE', dir : 'asc' }, { field : 'CB_POS', dir : 'asc' }]);
    }

    async runQuery_doGetChampLibreItems_Ligne() : Promise<RxCbSysLibreDocument[]> {
        let CHAMP_ITEMS = await this.runQuery_doGetCbsyslibreItems('F_DOCLIGNE');
        return orderBy(CHAMP_ITEMS, [{ field : 'CB_POS', dir : 'asc' }]);
    }

    private displaySnackbarErrorMessage(_type : string, _message : string, _duration?: number){
        let _panelClass : string = '';
        let _errorMessage : string = '';
        let _alert : { type: string; action: string; message: string } = { type   : 'success', action : 'SUCCES', message: '' };
        switch(_type){
            case "SUCCESS" : _alert.type = 'success'; _alert.action = 'SUCCES'; _panelClass = 'ccsuccess-snackbar'; _errorMessage = _message; break;
            case "ERROR" : _alert.type = 'error'; _alert.action = 'ERREUR'; _panelClass = 'ccerror-snackbar'; _errorMessage = _message; break;
            case "INFO" : _alert.type = 'info'; _alert.action = 'INFO'; _panelClass = 'ccinfo-snackbar'; _errorMessage = _message; break;
            case "WARN" : _alert.type = 'warn'; _alert.action = 'ATTENTION'; _panelClass = 'ccwarn-snackbar'; _errorMessage = _message; break;
        }
        this._snackbar.open(_errorMessage, _alert.action, { duration : _duration || 10000, panelClass : [_panelClass] });
    }

    async runQuery_doGetCluDoMoreParams(CT_NUM : string) : Promise<CluDoMoreParams> {
        let DO_MORE_PARAMS = new CluDoMoreParams();
        const P_DOSSIER: any = await this.runQuery_doGetPDossierItem();
        if(P_DOSSIER) {
            DO_MORE_PARAMS.DOSSIER_ITEM = new CluDoMoreParams_DossierItem();
            DO_MORE_PARAMS.DOSSIER_ITEM.D_RAISONSOC = P_DOSSIER.D_RAISONSOC;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_PROFESSION = P_DOSSIER.D_PROFESSION;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_ADRESSE = P_DOSSIER.D_ADRESSE;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_COMPLEMENT = P_DOSSIER.D_COMPLEMENT;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_CODEPOSTAL = P_DOSSIER.D_CODEPOSTAL;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_VILLE = P_DOSSIER.D_VILLE;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_CODEREGION = P_DOSSIER.D_CODEREGION;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_PAYS = P_DOSSIER.D_PAYS;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_EMAIL = P_DOSSIER.D_EMAIL;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_TELEPHONE = P_DOSSIER.D_TELEPHONE;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_TELECOPIE = P_DOSSIER.D_TELECOPIE;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_SITE = P_DOSSIER.D_SITE;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_SIRET = P_DOSSIER.D_SIRET;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_APE = P_DOSSIER.D_APE;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_IDENTIFIANT = P_DOSSIER.D_IDENTIFIANT;
            DO_MORE_PARAMS.DOSSIER_ITEM.D_RCS = P_DOSSIER.D_RCS;
        }
        const F_COMPTET: any = await this.runQuery_doGetClientItemByCtNum(CT_NUM);
        if(F_COMPTET) {
            DO_MORE_PARAMS.TIERS_ITEM = new CluDoMoreParams_TiersItem();
            DO_MORE_PARAMS.TIERS_ITEM.CT_NUM = F_COMPTET.CT_NUM;
            DO_MORE_PARAMS.TIERS_ITEM.N_CATTARIFLIB = F_COMPTET.N_CATTARIFLIB;
            DO_MORE_PARAMS.TIERS_ITEM.N_CATCOMPTALIB = F_COMPTET.N_CATCOMPTALIB;
            DO_MORE_PARAMS.TIERS_ITEM.CT_IDENTIFIANT = F_COMPTET.CT_IDENTIFIANT;
            DO_MORE_PARAMS.TIERS_ITEM.CT_APE = F_COMPTET.CT_APE;
            DO_MORE_PARAMS.TIERS_ITEM.CT_SIRET = F_COMPTET.CT_SIRET;
            DO_MORE_PARAMS.TIERS_ITEM.CT_TELEPHONE = F_COMPTET.CT_TELEPHONE;
            DO_MORE_PARAMS.TIERS_ITEM.CT_TELECOPIE = F_COMPTET.CT_TELECOPIE;
            DO_MORE_PARAMS.TIERS_ITEM.CT_EMAIL = F_COMPTET.CT_EMAIL;
            DO_MORE_PARAMS.TIERS_ITEM.CT_SITE = F_COMPTET.CT_SITE;
            DO_MORE_PARAMS.TIERS_ITEM.CT_ADRESSE = F_COMPTET.CT_ADRESSE;
            DO_MORE_PARAMS.TIERS_ITEM.CT_COMPLEMENT = F_COMPTET.CT_COMPLEMENT;
            DO_MORE_PARAMS.TIERS_ITEM.CT_VILLE = F_COMPTET.CT_VILLE;
            DO_MORE_PARAMS.TIERS_ITEM.CT_PAYS = F_COMPTET.CT_PAYS;
            DO_MORE_PARAMS.TIERS_ITEM.CT_CODEPOSTAL = F_COMPTET.CT_CODEPOSTAL;
            DO_MORE_PARAMS.TIERS_ITEM.CT_CODEREGION = F_COMPTET.CT_CODEREGION;
            DO_MORE_PARAMS.TIERS_ITEM.CO_NOLIB = F_COMPTET.CO_NOLIB;
            DO_MORE_PARAMS.TIERS_ITEM.CT_QUALITE = F_COMPTET.CT_QUALITE;
        }
        return DO_MORE_PARAMS;
    }

    async runQuery_doGetCluLastTickItem() : Promise<CluTickItem | null> {
        let ENTETE_LASTTICKET: any = null;
        if (this._tauriService.isTauri) {
            ENTETE_LASTTICKET = await this._tauriService.doGetFirstCluTickEnteteAction({
                MD_CAISSE_SESSION : this.mdItem.MD_CAISSE_SESSION,
                ZTICKET_STATUS : [CLU_TICKET_STATUS.TICKET_ACTIVE],
                SORT_COLUMN : 'DO_DATE',
                SORT_DIRECTION : 'DESC'
            });
        } else {
            const query_ENTETE_LASTTICKET = this._cashDeskRxDbService.db.clutickentete.findOne({
                selector: {
                    "MD_CAISSE_SESSION" : { "$eq": this.mdItem.MD_CAISSE_SESSION },
                    "ZTICKET_STATUS": { "$eq": CLU_TICKET_STATUS.TICKET_ACTIVE }
                },
                sort: [{ "DO_DATE": 'desc' }]
            });
            ENTETE_LASTTICKET = await query_ENTETE_LASTTICKET.exec();
        }
        if (ENTETE_LASTTICKET == undefined) return null;
        return await this.runQuery_doGetCluTickItem(ENTETE_LASTTICKET.DO_PIECE_INTERNAL);
    }

    async runQuery_doGetCluZTickTraceItems(DO_PIECE_INTERNAL : string) : Promise<RxCluZTickTraceDocument[]> {
        let TICKTRACE_ITEMS = await this.runQuery_doGetCluzticktraceItems(DO_PIECE_INTERNAL);
        return TICKTRACE_ITEMS;
    }

    async runQuery_modalCheckAndRestoreTicketsFromVaultRequired() {
        let _needRestore : boolean = false;
        if(!(document.title.indexOf('♛') > -1)) { this.displaySnackbarErrorMessage("WARN", "Votre caisse doit être ouverte dans un seul onglet du navigateur pour éditer un ticket."); return _needRestore; }

        const DEVICE = await this.runQuery_doGetDeviceItem(localStorage.getItem('LS_FE_AUTH_CASHDESK_GID')!);
        if(DEVICE && DEVICE.CAISSE_SESSION_ETAT == 1 && DEVICE.CAISSE_SESSION) {
            let _perimetre : ZTicketVaultDocumentsPerimetre = new ZTicketVaultDocumentsPerimetre();
            _perimetre.CAISSE_SESSION_ITEMS = [DEVICE.CAISSE_SESSION];
            let VAULT_TICKET_ROWS = await this._zTicketVaultDaoService.doGetTicketItems_ZTicketVault(_perimetre);
            for(const VAULT_TICKET_ROW of VAULT_TICKET_ROWS) {
                let VAULT_TICKET = VAULT_TICKET_ROW.ticket;
                if(!VAULT_TICKET || !VAULT_TICKET.ENTETE) continue;
                const query_ENTETE_LOCAL_TICKET_MD_DOGID = this._cashDeskRxDbService.db.clutickentete.findOne({ selector: { "MD_DOGID": { "$eq": VAULT_TICKET_ROW.MD_DOGID } }, sort: [] });
                let ENTETE_LOCAL_TICKET_MD_DOGID = await query_ENTETE_LOCAL_TICKET_MD_DOGID.exec();
                const query_ENTETE_LOCAL_TICKET_DO_PIECE_INTERNAL = this._cashDeskRxDbService.db.clutickentete.findOne({ selector: { "DO_PIECE_INTERNAL": { "$eq": VAULT_TICKET_ROW.DO_PIECE_INTERNAL } }, sort: [] });
                let ENTETE_LOCAL_TICKET_DO_PIECE_INTERNAL = await query_ENTETE_LOCAL_TICKET_DO_PIECE_INTERNAL.exec();
                if(ENTETE_LOCAL_TICKET_MD_DOGID == null && ENTETE_LOCAL_TICKET_DO_PIECE_INTERNAL == null) {
                    _needRestore = true;
                }
            }
        }
        return _needRestore;
    }

    // #region UTILS
    private doRoundToActiveFormat(num : number) : number {
        let _format = sessionStorage.getItem('CAURIS_LINKUP_FORMAT') || '';
        if(!_format) return num;
        let _pipe = new CustomNumberPipe();
        let _nbDecimales = _pipe.recupererNombreDecimales(_format);
        return this.doRound(num, _nbDecimales);
    }

    private doRound(num : number, decimalPlaces = 0) {
        var p = Math.pow(10, decimalPlaces);
        var n = (num * p) * (1 + Number.EPSILON);
        return Math.round(n) / p;
    }
    // #endregion
}
